import { CURRENT_CHAIN } from '@hyperswap/addresses'
import { Currency, Price, WETH9 } from '@uniswap/sdk-core'
import { FeeAmount } from '@uniswap/v3-sdk'
import axios from 'axios'
import Loader from 'components/Loader'
import TagButton from 'components/TagButton'
import { API_POOL_BASE_URL } from 'constants/links'
import { useActiveWeb3React } from 'hooks'
import { useV3Factory } from 'hooks/useContract'
import { PresetPriceRange } from 'pages/AddLiquidity'
import { ChartHeader, ChartHeaderTitle, ChartTitle, TooltipContainer } from 'pages/ExplorePools/Metrics/styled'
import React from 'react'
import {
  Bar,
  BarChart,
  Cell,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from 'recharts'
import styled from 'styled-components'
import { wrappedCurrency } from 'utils/wrappedCurrency'

const ContainerStyle = styled.div`
  animation: fadeIn 0.5s ease-in-out;
  cursor: default;
  width: 100%;
  height: 226px;
  max-height: 250px;
  padding: 16px;
  border-radius: 16px;
  background: ${({ theme }) => theme.bg1};
  overflow: hidden;

  .recharts-surface {
    overflow: visible;
  }
`

interface ChartRangeProps {
  currencyIdA: string | undefined
  currencyIdB: string | undefined
  tokenASymbol: string | null
  tokenBSymbol: string | null
  feeAmount: FeeAmount | undefined
  currencyA: Currency
  currencyB: Currency
  price: any
  priceLower?: Price
  priceUpper?: Price
  onLeftRangeInput: (typedValue: string) => void
  onRightRangeInput: (typedValue: string) => void
  presetPriceRange: PresetPriceRange
  invertedFromAPI: boolean
  setInvertedFromAPI: (value: boolean) => void
}

function isInverted(uiCurrentPrice: number, price: number, invertedPrice: number): boolean {
  const diffA = Math.abs(uiCurrentPrice - price)
  const diffB = Math.abs(uiCurrentPrice - invertedPrice)

  return diffA < diffB ? false : true
}

const ChartRange: React.FC<ChartRangeProps> = ({
  currencyIdA,
  currencyIdB,
  feeAmount,
  tokenASymbol,
  tokenBSymbol,
  currencyA,
  currencyB,
  price,
  priceLower,
  priceUpper,
  onLeftRangeInput,
  onRightRangeInput,
  presetPriceRange,
  setInvertedFromAPI,
  invertedFromAPI,
}) => {
  const { library, chainId } = useActiveWeb3React()

  const factoryContract = useV3Factory()
  const [loading, setLoading] = React.useState(false)
  const [data, setData] = React.useState<any[]>([])
  const [loadingLiquidity, setLoadingLiquidity] = React.useState(false)
  const [liquidityData, setLiquidityData] = React.useState<any[]>([])
  const [pairAddress, setPairAddress] = React.useState<null | string>(null)
  const tokenA = wrappedCurrency(currencyA, chainId)
  const tokenB = wrappedCurrency(currencyB, chainId)
  const chartRef = React.useRef<any>(null)

  const isSorted = tokenA && tokenB && tokenA.address !== tokenB.address && tokenA.sortsBefore(tokenB)
  const chartTokenBaseName = currencyB.symbol + '/' + currencyA.symbol

  // Track previous input values
  const previousInputs = React.useRef<{
    currencyIdA?: string
    currencyIdB?: string
    price?: any
    feeAmount?: FeeAmount
  }>({})

  React.useEffect(() => {
    const fetchData = async () => {
      if (!factoryContract || !currencyIdA || !currencyIdB || !feeAmount || !library) {
        return
      }

      const tokenAAddress = currencyIdA === 'HYPE' ? WETH9[CURRENT_CHAIN].address : currencyIdA
      const tokenBAddress = currencyIdB === 'HYPE' ? WETH9[CURRENT_CHAIN].address : currencyIdB

      try {
        setLoadingLiquidity(true)

        const pairAddress = await factoryContract.getPool(tokenAAddress, tokenBAddress, feeAmount)

        setPairAddress(pairAddress)
        const response = await axios.get(`${API_POOL_BASE_URL}/api/market/pair-depth`, {
          params: {
            pairAddress,
          },
        })

        setLiquidityData(response?.data?.data ? response?.data?.data : [])
        setLoadingLiquidity(false)
      } catch (e: any) {
        console.warn(e)
        setLiquidityData([])
        setLoadingLiquidity(false)
      } finally {
        setLoadingLiquidity(false)
      }
    }

    if (liquidityData.length === 0 && !loadingLiquidity) {
      fetchData()
    }
  }, [
    factoryContract,
    currencyIdA,
    currencyIdB,
    feeAmount,
    library,
    tokenASymbol,
    tokenBSymbol,
    liquidityData.length,
    loadingLiquidity,
  ])

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (chartRef.current) {
        chartRef.current?.setState({})
      }
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  React.useEffect(() => {
    // dirty isInversed logic
    const dataArray = data?.[0]?.data

    if (dataArray?.length > 0) {
      const dataWithoutEmpty = dataArray.filter((item: any[]) => item[4] && item[4] > 0)
      const middleValue = dataWithoutEmpty[Math.round(dataWithoutEmpty.length / 2)]
      if (middleValue) {
        const priceAPI = middleValue[4]
        const invertedPrice = 1 / middleValue[4]

        if (price && invertedPrice && priceAPI) {
          const hasToInvert = isInverted(Number(price), priceAPI, invertedPrice)
          setInvertedFromAPI(hasToInvert)
        } else {
          setInvertedFromAPI(false)
        }
      } else {
        setInvertedFromAPI(false)
      }
    }
  }, [data, price, setInvertedFromAPI])

  React.useEffect(() => {
    const fetchData = async () => {
      if (!factoryContract || !currencyIdA || !currencyIdB || !feeAmount || !library) {
        return
      }

      const tokenAAddress = currencyIdA === 'HYPE' ? WETH9[CURRENT_CHAIN].address : currencyIdA
      const tokenBAddress = currencyIdB === 'HYPE' ? WETH9[CURRENT_CHAIN].address : currencyIdB

      try {
        setLoading(true)

        const pairAddress = await factoryContract.getPool(tokenAAddress, tokenBAddress, feeAmount)

        const response = await axios.post(`${API_POOL_BASE_URL}/api/market/batch-candles`, {
          candleInputs: [
            {
              pair: chartTokenBaseName,
              interval: '15m',
              exchange: pairAddress.toLowerCase(),
              limit: 300,
            },
          ],
        })

        setData(response?.data?.data ? response?.data?.data : [])
        setLoading(false)
      } catch (e: any) {
        console.warn(e)
        setData([])
        setLoading(false)
      } finally {
        setLoading(false)
      }
    }

    const sameAsPrevious =
      previousInputs.current.currencyIdA === currencyIdA &&
      previousInputs.current.currencyIdB === currencyIdB &&
      previousInputs.current.feeAmount === feeAmount &&
      previousInputs.current.price === price

    if (sameAsPrevious) return
    previousInputs.current = { currencyIdA, currencyIdB, feeAmount }

    if (!sameAsPrevious && loading === false && data.length === 0) {
      fetchData()
    }
  }, [
    factoryContract,
    currencyIdA,
    currencyIdB,
    feeAmount,
    library,
    tokenASymbol,
    tokenBSymbol,
    chartTokenBaseName,
    price,
    setInvertedFromAPI,
    loading,
    data,
  ])

  const priceData = data?.[0]?.data

  const shouldInvert = invertedFromAPI

  // Hard
  const formattedData = priceData
    ?.filter((item: any[]) => item[4] && item[4] > 0)
    .map((item: any[]) => ({
      timestamp: item[0] * 1000,
      close: shouldInvert ? 1 / item[4] : item[4],
    }))

  const latestClose = formattedData?.[formattedData.length - 1]?.close
  const closePrices = formattedData?.map((d: any) => d.close) ?? []
  const minPrice = Math.min(...closePrices)
  const maxPrice = Math.max(...closePrices)

  const distanceToMin = Math.abs(latestClose - minPrice)
  const distanceToMax = Math.abs(maxPrice - latestClose)
  const maxDistance = Math.max(distanceToMin, distanceToMax)

  const isValidNumber = (n: any) => typeof n === 'number' && !isNaN(n)

  const yDomain =
    isValidNumber(latestClose) && isValidNumber(maxDistance)
      ? [latestClose - maxDistance, latestClose + maxDistance]
      : ['auto', 'auto']

  const priceLowerSorted = isSorted ? priceLower?.toSignificant() : priceUpper?.invert()?.toSignificant()
  const priceUpperSorted = isSorted ? priceUpper?.toSignificant() : priceLower?.invert()?.toSignificant()
  const yScale = (value: number) => {
    const chart = chartRef.current
    const scale = chart?.state?.yAxisMap?.['0']?.scale
    return typeof scale === 'function' ? scale(value) : 0
  }

  const startDrag = (e: React.MouseEvent<SVGRectElement, MouseEvent>, type: 'min' | 'max') => {
    const svg = e.currentTarget.ownerSVGElement
    if (!svg) return

    const onMouseMove = (moveEvent: MouseEvent) => {
      const svgRect = svg.getBoundingClientRect()
      const y = moveEvent.clientY - svgRect.top
      const chartHeight = svgRect.height
      const pctFromTop = y / chartHeight
      const value = yDomain[1] - pctFromTop * (yDomain[1] - yDomain[0])

      if (type === 'min') {
        onLeftRangeInput(value.toFixed(6))
      } else {
        onRightRangeInput(value.toFixed(6))
      }
    }

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }

  const transformedDataLiquidity = React.useMemo(() => {
    if (!shouldInvert) {
      return [...liquidityData].sort((a: any, b: any) => Number(a.price) - Number(b.price))
    }

    return [...liquidityData]
      .map((item: any) => ({
        ...item,
        price: 1 / Number(item.price),
        side: item.side === 'bid' ? 'ask' : 'bid',
      }))
      .sort((a, b) => Number(a.price) - Number(b.price))
  }, [liquidityData, shouldInvert])
  return (
    <>
      {loading === true ? (
        <>
          <ContainerStyle style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader size="32px" />
          </ContainerStyle>
        </>
      ) : (
        <>
          <>
            {priceData?.length > 0 ? (
              <ContainerStyle>
                <ChartHeader>
                  <ChartHeaderTitle>
                    <ChartTitle>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                        Price chart{' '}
                      </div>
                    </ChartTitle>
                  </ChartHeaderTitle>
                </ChartHeader>
                <ResponsiveContainer width="100%" height={185}>
                  <LineChart data={formattedData} ref={chartRef}>
                    <XAxis
                      dataKey="timestamp"
                      type="number"
                      domain={['auto', 'auto']}
                      tickFormatter={(time: number) => {
                        const date = new Date(time)
                        return date.toLocaleString('en-US', {
                          month: 'short',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false,
                        })
                      }}
                      tick={{ fontSize: 10, fill: '#C3C5CB' }}
                      minTickGap={35}
                    />
                    <YAxis
                      dataKey="close"
                      domain={yDomain}
                      tick={{ fontSize: 10, fill: '#C3C5CB' }}
                      orientation="right"
                      tickFormatter={(value) => value.toFixed(5)}
                    />
                    <ReferenceLine
                      y={latestClose}
                      stroke="#C3C5CB"
                      strokeDasharray="3 3"
                      ifOverflow="extendDomain"
                      label={<LastPriceLabel value={price} />}
                    />

                    {priceLowerSorted && priceUpperSorted && presetPriceRange === null && (
                      <>
                        {/* Drag icon below lower price line */}
                        <foreignObject
                          x="calc(50% - 50px)"
                          y={yScale(parseFloat(priceLowerSorted)) + 6}
                          width="30"
                          height="12"
                          style={{ pointerEvents: 'none' }}
                        >
                          <div
                            style={{
                              width: '30px',
                              height: '4px',
                              backgroundColor: '#AAA',
                              borderRadius: '2px',
                              margin: 'auto',
                            }}
                          />
                        </foreignObject>

                        {/* Lower price line */}
                        <ReferenceLine
                          y={parseFloat(priceLowerSorted)}
                          stroke="#AAA"
                          strokeDasharray="1 1"
                          ifOverflow="extendDomain"
                          isFront
                          label={{
                            value: '↑',
                            position: 'left',
                            fontSize: 10,
                            fill: 'white',
                          }}
                        />
                        <rect
                          x={0}
                          y={yScale(parseFloat(priceLowerSorted)) - 16}
                          width="100%"
                          height={32}
                          fill="transparent"
                          cursor="ns-resize"
                          style={{ pointerEvents: 'all' }}
                          onMouseDown={(e: any) => startDrag(e, 'min')}
                        />

                        {/* Drag icon above upper price line */}
                        <foreignObject
                          x="calc(50% - 50px)"
                          y={yScale(parseFloat(priceUpperSorted)) - 10}
                          width="30"
                          height="12"
                          style={{ pointerEvents: 'none' }}
                        >
                          <div
                            style={{
                              width: '30px',
                              height: '4px',
                              backgroundColor: '#AAA',
                              borderRadius: '2px',
                              margin: 'auto',
                            }}
                          />
                        </foreignObject>

                        {/* Upper price line */}
                        <ReferenceLine
                          y={parseFloat(priceUpperSorted)}
                          stroke="#AAA"
                          strokeDasharray="1 1"
                          ifOverflow="extendDomain"
                          isFront
                          label={{
                            value: '↓',
                            position: 'left',
                            fontSize: 10,
                            fill: 'white',
                          }}
                        />
                        <rect
                          x={0}
                          y={yScale(parseFloat(priceUpperSorted)) - 16}
                          width="100%"
                          height={32}
                          fill="transparent"
                          cursor="ns-resize"
                          onMouseDown={(e: any) => startDrag(e, 'max')}
                        />

                        {/* Highlight range */}
                        <ReferenceArea
                          y1={parseFloat(priceLowerSorted)}
                          y2={parseFloat(priceUpperSorted)}
                          stroke="none"
                          fill="#4afb3920"
                          ifOverflow="extendDomain"
                        />
                      </>
                    )}

                    {presetPriceRange === 'maxRange' && isValidNumber(yDomain[0]) && isValidNumber(yDomain[1]) && (
                      <>
                        <ReferenceArea
                          y1={yDomain[0]}
                          y2={yDomain[1]}
                          stroke="none"
                          fill="#4afb3910"
                          ifOverflow="extendDomain"
                        />
                        <ReferenceLine
                          y={yDomain[0]}
                          stroke="#4AFB39"
                          strokeDasharray="1 1"
                          ifOverflow="extendDomain"
                          label={<InfinitePriceLabel value={'≈ 0'} />}
                        />
                        <ReferenceLine
                          y={yDomain[1]}
                          stroke="#4AFB39"
                          strokeDasharray="1 1"
                          ifOverflow="extendDomain"
                          label={<InfinitePriceLabel value={'∞'} />}
                        />
                      </>
                    )}
                    {presetPriceRange !== null &&
                      presetPriceRange !== 'maxRange' &&
                      priceLowerSorted &&
                      priceUpperSorted && (
                        <>
                          <ReferenceLine
                            y={parseFloat(priceLowerSorted)}
                            stroke="#4AFB39"
                            strokeDasharray="1 1"
                            ifOverflow="extendDomain"
                            isFront
                            label={{
                              value: '↑',
                              position: 'left',
                              fontSize: 10,
                              fill: 'white',
                            }}
                          />
                          <ReferenceLine
                            y={parseFloat(priceUpperSorted)}
                            stroke="#4AFB39"
                            strokeDasharray="1 1"
                            ifOverflow="extendDomain"
                            isFront
                            label={{
                              value: '↓',
                              position: 'left',
                              fontSize: 10,
                              fill: 'white',
                            }}
                          />
                          <ReferenceArea
                            y1={parseFloat(priceLowerSorted)}
                            y2={parseFloat(priceUpperSorted)}
                            stroke="none"
                            fill="#4afb3920"
                            ifOverflow="extendDomain"
                          />
                        </>
                      )}
                    <Line type="monotone" dataKey="close" stroke="#4AFB39A1" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </ContainerStyle>
            ) : (
              <></>
            )}
          </>
          <>
            {loadingLiquidity === true ? (
              <>
                <ContainerStyle style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Loader size="32px" />
                </ContainerStyle>
              </>
            ) : (
              <>
                {transformedDataLiquidity.length > 0 ? (
                  <ContainerStyle>
                    <ChartHeader>
                      <ChartHeaderTitle>
                        <ChartTitle>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            Depth chart{' '}
                            <TagButton
                              onClick={() => window.open(`https://purrsec.com/address/${pairAddress}`, '_blank')}
                            >
                              <svg
                                viewBox="0 0 16 16"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ width: '18px', background: 'black', borderRadius: '100px' }}
                              >
                                <line x1="1" y1="5" x2="15" y2="12" strokeWidth="1" stroke="currentColor"></line>
                                <line x1="1" y1="11" x2="15" y2="9" strokeWidth="1" stroke="currentColor"></line>
                              </svg>
                              View on Purrsec
                            </TagButton>
                          </div>
                        </ChartTitle>
                      </ChartHeaderTitle>
                    </ChartHeader>
                    <ResponsiveContainer width="100%" height={185}>
                      <BarChart data={transformedDataLiquidity} barSize={20}>
                        <XAxis
                          dataKey="price"
                          tickFormatter={(value) => Number(value).toFixed(5)}
                          tick={{ fontSize: 10, fill: '#C3C5CB' }}
                        />

                        <YAxis dataKey="qty" tick={{ fontSize: 10, fill: '#C3C5CB' }} orientation="right" />
                        <defs>
                          <linearGradient id="colorbid" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#4AFB39" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#4AFB39" stopOpacity={0.3} />
                          </linearGradient>
                          <linearGradient id="colorAsk" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#FD4040" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#FD4040" stopOpacity={0.3} />
                          </linearGradient>
                        </defs>
                        <Bar dataKey="qty">
                          {transformedDataLiquidity.map((entry: any, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={entry.side === 'ask' ? 'url(#colorAsk)' : 'url(#colorbid)'}
                              fillOpacity={0.9}
                            />
                          ))}
                        </Bar>

                        {presetPriceRange === 'maxRange' && transformedDataLiquidity.length > 0 ? (
                          <>
                            <ReferenceLine
                              x={transformedDataLiquidity[0].price}
                              stroke="#4AFB39"
                              strokeDasharray="1 1"
                              ifOverflow="extendDomain"
                              isFront={true}
                              strokeWidth={1}
                            />
                            <ReferenceLine
                              x={transformedDataLiquidity[transformedDataLiquidity.length - 1].price}
                              stroke="#4AFB39"
                              strokeDasharray="1 1"
                              ifOverflow="extendDomain"
                              isFront={true}
                              strokeWidth={1}
                            />
                            <ReferenceArea
                              x1={transformedDataLiquidity[0].price}
                              x2={transformedDataLiquidity[transformedDataLiquidity.length - 1].price}
                              stroke="none"
                              fill="#4afb3920"
                              ifOverflow="extendDomain"
                            />
                          </>
                        ) : (
                          priceLowerSorted &&
                          priceUpperSorted &&
                          transformedDataLiquidity.length > 0 && (
                            <>
                              <ReferenceLine
                                x={
                                  transformedDataLiquidity.reduce((prev, curr) =>
                                    Math.abs(Number(curr.price) - Number(priceLowerSorted)) <
                                    Math.abs(Number(prev.price) - Number(priceLowerSorted))
                                      ? curr
                                      : prev
                                  ).price
                                }
                                stroke="#4AFB39"
                                strokeDasharray="1 1"
                                ifOverflow="extendDomain"
                                isFront={true}
                                strokeWidth={1}
                              />
                              <ReferenceLine
                                x={
                                  transformedDataLiquidity.reduce((prev, curr) =>
                                    Math.abs(Number(curr.price) - Number(priceUpperSorted)) <
                                    Math.abs(Number(prev.price) - Number(priceUpperSorted))
                                      ? curr
                                      : prev
                                  ).price
                                }
                                stroke="#4AFB39"
                                strokeDasharray="1 1"
                                ifOverflow="extendDomain"
                                isFront={true}
                                strokeWidth={1}
                              />
                              <ReferenceArea
                                x1={
                                  transformedDataLiquidity.reduce((prev, curr) =>
                                    Math.abs(Number(curr.price) - Number(priceLowerSorted)) <
                                    Math.abs(Number(prev.price) - Number(priceLowerSorted))
                                      ? curr
                                      : prev
                                  ).price
                                }
                                x2={
                                  transformedDataLiquidity.reduce((prev, curr) =>
                                    Math.abs(Number(curr.price) - Number(priceUpperSorted)) <
                                    Math.abs(Number(prev.price) - Number(priceUpperSorted))
                                      ? curr
                                      : prev
                                  ).price
                                }
                                stroke="none"
                                fill="#4afb3920"
                                ifOverflow="extendDomain"
                              />
                            </>
                          )
                        )}

                        <Tooltip
                          cursor={{ fill: '#1F1C29A1' }}
                          content={({ payload }) => {
                            if (payload && payload.length > 0) {
                              const data = payload[0].payload

                              return (
                                <TooltipContainer>
                                  <div className="tooltip__volume">
                                    <span className="tooltip__volume-title">Price</span>
                                    <span className="tooltip__volume-value">{data.price}</span>
                                    <span className="tooltip__volume-title">Liquidity</span>
                                    <span className="tooltip__volume-value">{data.qty}</span>
                                  </div>
                                </TooltipContainer>
                              )
                            }
                            return null
                          }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </ContainerStyle>
                ) : (
                  <></>
                )}
              </>
            )}
          </>
        </>
      )}
    </>
  )
}
export default ChartRange

const LastPriceLabel: React.FC<{ value?: string | number; viewBox?: any }> = ({ value, viewBox }) => {
  const { y } = viewBox
  const rectHeight = 18
  const padding = 10
  const charWidth = 5
  const rectWidth = value ? String(value).length * charWidth + padding * 2 : 50
  const x = 10

  if (y && value) {
    return (
      <g transform={`translate(${x}, ${y})`}>
        <rect x={0} y={-rectHeight / 2} width={rectWidth} height={rectHeight} rx={6} ry={6} fill="#4AFB39" />
        <text x={rectWidth / 2} y={4} textAnchor="middle" fill="black" fontSize="10">
          {value}
        </text>
      </g>
    )
  } else return <></>
}

const InfinitePriceLabel: React.FC<{ value?: string | number; viewBox?: any }> = ({ value, viewBox }) => {
  const { y } = viewBox
  const rectHeight = 16
  const padding = 10
  const charWidth = 2
  const rectWidth = value ? String(value).length * charWidth + padding * 2 : 50
  const x = 10

  if (value && viewBox) {
    return (
      <g transform={`translate(${x}, ${y})`}>
        <rect x={0} y={-rectHeight / 2} width={rectWidth} height={rectHeight} rx={6} ry={6} fill="#4AFB39" />
        <text x={rectWidth / 2} y={4} textAnchor="middle" fill="black" fontSize="10">
          {value}
        </text>
      </g>
    )
  } else {
    return <></>
  }
}
